/**
 * Блок прозрачности: что опубликовано и когда это можно проверить.
 *
 * Показываем коммит всегда, сид — только после раскрытия. До раскрытия честный
 * текст объясняет, почему сида нет: если бы он был виден во время акции, любой
 * мог бы заранее вычислить выигрышные коды. Это не ограничение, а механика.
 */
import type { FairCommit } from '@allswipes/client';
import { Badge, Card, Hash, KeyRow, fmtDateTime, num } from '@allswipes/ui';
import { Link } from 'react-router-dom';

import { useT } from '@allswipes/i18n';

export function Fairness({
  fair,
  campaignId,
  hasDraw,
  random = true,
}: {
  fair?: FairCommit;
  campaignId: string;
  hasDraw: boolean;
  /**
   * Есть ли в механике случайность. Если нет — обещание «сид раскроется после
   * акции» становится выдумкой: исход не считается сидом, и пересчитывать
   * человеку нечего. Доказывать здесь можно только то, что правила не
   * переписаны, а счётчик считается из тех же строк, по которым начисляется
   * награда.
   */
  random?: boolean;
}) {
  const t = useT();
  if (!fair) return null;
  const revealed = Boolean(fair.revealed_at && fair.server_seed);

  if (!random) {
    return (
      <Card>
        <div className="row" style={{ marginBottom: 10 }}>
          <span className="h2">{t('Честность')}</span>
          <span className="spacer" />
          <Badge>{t('без случайности')}</Badge>
        </div>
        <p className="sub">
          {t('Платформа ничего не разыгрывает: результат определён условиями, объявленными до старта. Сида в этой механике нет, и его хеш ничего бы не доказывал.')}
        </p>
        <p className="sub" style={{ marginTop: 8 }}>
          {t('Проверяется другое: счётчик считается из тех же строк, по которым начисляется награда, а хеш правил не даёт переписать условия задним числом.')}
        </p>
        <Link to={`/rules/${campaignId}`} className="btn out block" style={{ marginTop: 12 }}>
          {t('Правила акции')}
        </Link>
      </Card>
    );
  }

  return (
    <Card>
      <div className="row" style={{ marginBottom: 10 }}>
        <span className="h2">{t('Честность')}</span>
        <span className="spacer" />
        <Badge tone={revealed ? 'good' : undefined}>
          {revealed ? t('можно проверить') : t('сид ещё закрыт')}
        </Badge>
      </div>

      <KeyRow label={t('Алгоритм')}>{fair.algo_version}</KeyRow>
      <KeyRow label={t('Хеш сида сервера')} mono>
        <Hash value={fair.server_seed_hash} />
      </KeyRow>
      {fair.ticket_root && (
        <>
          <KeyRow label={t('Корень списка билетов')} mono>
            <Hash value={fair.ticket_root} />
          </KeyRow>
          <KeyRow label={t('Билетов зафиксировано')}>{num(fair.ticket_count)}</KeyRow>
        </>
      )}
      {fair.external_value && (
        <KeyRow
            label={`${t('Публичный сид')}${fair.external_source ? ` · ${fair.external_source}` : ''}`}
            mono
          >
          {fair.external_value}
        </KeyRow>
      )}
      {revealed && (
        <KeyRow label={t('Сид сервера раскрыт')} mono>
          <Hash value={fair.server_seed!} />
        </KeyRow>
      )}
      {fair.revealed_at && <KeyRow label={t('Раскрыт')}>{fmtDateTime(fair.revealed_at)}</KeyRow>}

      <p className="sub" style={{ marginTop: 12 }}>
        {revealed
          ? t('Сид раскрыт. Пересчитай результат сам — мы ничего не подставляем.')
          : hasDraw
            ? t('Хеш сида опубликован до старта, поэтому результат нельзя изменить задним числом. Сам сид раскроем вместе с итогами розыгрыша.')
            : t('Хеш сида опубликован до старта. Сам сид закрыт до конца акции: иначе можно было бы заранее посчитать, какие коды выигрышные.')}
      </p>

      <Link to={`/verify/${campaignId}`} className="btn out block" style={{ marginTop: 12 }}>
        {t('Открыть верификатор')}
      </Link>
    </Card>
  );
}
